/* eslint-disable camelcase */
import { HttpHeaders, HttpResponse } from '@capacitor/core';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes.js';
import {
	FetchBaseQueryArgs,
	FetchBaseQueryError,
	FetchBaseQueryMeta
} from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { HttpRequest } from '../App.js';
import { refreshFetch } from '../state/loginSlice';

// Function to lowercase keys of http headers
function lowercaseKeys(obj: { [key: string]: string }): { [key: string]: string } {
	const keys = Object.keys(obj);
	const newobj: Record<string, string> = {};
	for (const key of keys) {
		newobj[key.toLowerCase()] = obj[key];
	}
	return newobj;
}
export type CookieOption = {
	value: string;
	expires?: Date;
	maxAge?: number;
	domain?: string;
	path?: string;
	secure?: boolean;
	httpOnly?: boolean;
	sameSite?: string;
	[key: string]: string | number | boolean | Date | undefined;
};

export type ParsedCookies = {
	[name: string]: CookieOption;
};

interface FetchLikeOptions {
	/** A body */
	body?: string | { [key: string]: unknown };
	/** A string indicating how the request will interact with the browser's cache to set request's cache. */
	cache?: 'default' | 'force-cache' | 'no-cache' | 'no-store' | 'only-if-cached' | 'reload';
	/** A Headers object, an object literal, or an array of two-item arrays to set request's headers. */
	headers?: HttpHeaders;
	/** A cryptographic hash of the resource to be fetched by request. Sets request's integrity. */
	/** A string to set request's method. */
	method?: string;
	/** A string to indicate whether the request will use CORS, or will be restricted to same-origin URLs. Sets request's mode. */
}

type FetchLikeReponse = HttpResponse & { json: () => Promise<any>; statusText: string };

/**
 * Creates a fetch-like interface compatible with existing fetch logic in the app. DO NOT USE for new code or new fetches, use HttpRequest instead.
 * @param { string } url The URL to fetch
 * @param { FetchLikeOptions } options Fetch options
 * @returns { Promise<FetchLikeReponse>} A fetch-like response with .json() method
 * @deprecated Use HttpRequest instead
 */
export async function fetchLike(url: string, options: FetchLikeOptions): Promise<FetchLikeReponse> {
	const requestOptions = {
		url,
		method: options.method,
		data: typeof options.body === 'string' ? JSON.parse(options.body) : options.body,
		// Default content-type to application/json, but allow override, also normalize headers to lowercase
		headers: {
			'content-type': 'application/json',
			...lowercaseKeys((options.headers as HttpHeaders) || {})
		} as HttpHeaders
	};
	if (options.cache) {
		requestOptions.headers = { ...requestOptions.headers, 'cache-control': options.cache };
	}
	const result = await HttpRequest(requestOptions);
	const response: FetchLikeReponse = {
		...result,
		json: async () => {
			return result.data;
		},
		statusText: result.data
	};
	return response;
}

export type BaseQueryType = BaseQueryFn<
	{
		url: string;
		method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
		body?: unknown;
		headers?: Record<string, string>;
	},
	unknown,
	FetchBaseQueryError,
	{},
	FetchBaseQueryMeta
>;

// This is a wrapper around fetchBaseQuery that will automatically refresh the token if the request fails
// with a 401 Unauthorized error.
export const baseQueryWithReauth = ({ baseUrl }: FetchBaseQueryArgs = {}): BaseQueryType => {
	return async function ({
		url,
		method = 'GET',
		body,
		headers = {}
	}): Promise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>> {
		let result = await HttpRequest({
			url: baseUrl + url,
			method,
			data: body,
			headers: { 'cache-control': 'no-cache', 'content-type': 'application/json', ...lowercaseKeys(headers) }
		});

		if (result.status === 401) {
			// try to get a new token
			try {
				await refreshFetch();
				result = await HttpRequest({
					url: baseUrl + url,
					method,
					data: body,
					headers: { 'cache-control': 'no-cache', 'content-type': 'application/json' }
				});
			} catch (e) {
				// if we can't get a new token, return the original error
				return {
					error: { status: result.status, data: result.data }
				};
			}
		}
		// if requests was successful, return the result, else return the error

		if (result.status < 300 && result.status >= 200) {
			return { data: result.data };
		}
		return {
			error: { status: result.status, data: result.data }
		};
	};
};
